import { Button, List, message, notification, Select, Space, Typography } from 'antd';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import Form from '../components/Form';
import GeneralRequestJobPostingForm, {
  GeneralRequestJobPostingFormValues,
} from '../components/GeneralRequestJobPostingForm';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import useDiscardChangesModal from '../hooks/useDiscardChangesModal';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { withRouteChange } from '../services/APM';
import { RequestJobPostingTemplateCollection } from '../types';

const STALE_TIME = 1000 * 60 * 5;
interface CreateRequestJobPostingFormValues extends GeneralRequestJobPostingFormValues {
  requestJobPostingTemplateId: RequestJobPostingTemplateCollection['id'];
}

const CreateRequestJobPosting = () => {
  const { tenant } = useTypedParams({ tenant: String });
  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [setIsDirty] = useDiscardChangesModal(form);
  const selectedCompanyValue = Form.useWatch<string>('vixtingCompany', form);
  const selectedJobRoleValue = Form.useWatch<string>('vixtingJobRole', form);

  const requestJobPostingTemplateValue = Form.useWatch<number | undefined>(
    'requestJobPostingTemplateId',
    form
  );

  const requestJobPostingTemplatesPages = useInfiniteAPIQuery(['/request-job-posting-templates/']);
  const requestJobPostingTemplates = usePrefetchPages(
    requestJobPostingTemplatesPages,
    (data) => data.results
  );

  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const selectedRequestJobPostingTemplate = useAPIQuery(
    `/request-job-posting-templates/${requestJobPostingTemplateValue}/`,
    {
      enabled: !!requestJobPostingTemplateValue,
    }
  );

  const config = useAPIQuery('/configs/tenant-configurations/');

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      onError: () => {
        message.error('Falha ao carregar empresas da vixting!');
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar filiais da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar unidades de negócio da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar cargos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const selectedJob = vixtingJobRoles.find(
    (jobRole) => jobRole.id?.toString() === selectedJobRoleValue?.toString()
  );

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!selectedCompanyValue &&
        !!selectedJob?.linkToAllDepartment,
      staleTime: STALE_TIME,
      onError: () => {
        message.error('Falha ao carregar departamentos da vixting!');
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const mutation = useAPIMutation(
    async (variables: CreateRequestJobPostingFormValues, api) => {
      const requestJobPostingTemplate = await api.get(
        `/request-job-posting-templates/${variables.requestJobPostingTemplateId}/`
      );

      for (const stage of requestJobPostingTemplate.data.template_stages) {
        if (!stage.approver.is_active) {
          throw new Error('Inactive User');
        }
      }

      const requestJobPosting = await api.post('/request-job-postings/', {
        name: variables.name,
        type: variables.requestType,
        employee_name_being_replaced: variables.employeeNameBeingReplaced,
        positions: variables.positions,
        start_salary: variables.startSalaryRange,
        end_salary: variables.endSalaryRange,
        template_name: requestJobPostingTemplate.data.name,
        branch: variables.branch,
        description: variables.description,
        claimant_commentary: variables.annotation,
        stages: requestJobPostingTemplate.data.template_stages.map((stage) => ({
          name: stage.name,
          sequence: stage.sequence,
          approver: stage.approver.id,
        })),
      });

      for (const user of variables.relatedUsers || []) {
        await api.post('/request-job-posting-users/', {
          request_job_posting: requestJobPosting.data.id,
          user,
        });
      }

      if (config.data?.enableVixtingAdmission) {
        await api.post(`/vixting/vixting-request-job-postings/`, {
          request_job_posting: requestJobPosting.data?.id,
          company: variables.vixtingCompany,
          job_role: variables.vixtingJobRole,
          department: variables.vixtingDepartment,
          affiliate: variables.vixtingAffiliate,
          unit_business: variables.vixtingUnitBusiness,
        });
      }

      return requestJobPosting;
    },
    {
      onSettled: () => queryClient.invalidateQueries('/request-job-postings/'),
      onSuccess: (response) => {
        setIsDirty(false);
        // dispatch setIsDirty possui um delay para atualizar o estado do Modal de descarte, resolvido com uma mensagem antes do navigate
        message.success('Requisição de vaga criada');
        navigate({
          pathname: '/:tenant/request-job-postings/:id/analysis/',
          params: { tenant, id: response.data.id },
        });
      },
      onError: (error, variables, context) => {
        if (error.message === 'Inactive User') {
          notification.error({
            duration: 0,
            message:
              'Este modelo de requisição não pode ser selecionado porque há usuários desativados associados a ele. Para mais informações, entre em contato com o administrador do sistema.',
          });
        } else {
          message.error(error.message);
        }
      },
    }
  );

  const formArgs = {
    enableVixtingAdmission: config.data?.enableVixtingAdmission,
    enableVixtingUnitBusiness: config.data?.enableVixtingUnitBusiness,
    vixtingCompanies,
    vixtingJobRoles,
    vixtingAffiliates,
    vixtingUnitBusinesses,
    vixtingDepartments: selectedJob?.linkToAllDepartment
      ? vixtingAllDepartments
      : selectedJob?.departments || [],
  };

  return (
    <Page>
      <PageTitle title="Nova Requisição" />
      <PageContent>
        <Form<CreateRequestJobPostingFormValues>
          form={form}
          name="basic"
          layout="vertical"
          validateMessages={{
            required: 'Campo obrigatório.',
          }}
          onFinish={(variables) => mutation.mutate(variables)}
          onValuesChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <Form.Col span={12}>
            <Form.Item
              name="requestJobPostingTemplateId"
              label="Modelo de requisição"
              rules={[{ required: true }]}
            >
              <Select>
                {requestJobPostingTemplates.map((template) => (
                  <Select.Option value={template.id}>{template.name}</Select.Option>
                ))}
              </Select>
            </Form.Item>
            {selectedRequestJobPostingTemplate.data && (
              <List
                size="small"
                dataSource={selectedRequestJobPostingTemplate.data.template_stages}
                renderItem={(item) => (
                  <List.Item>
                    <Space direction="vertical">
                      <Typography.Text>
                        {item.sequence + 1}. {item.name}
                      </Typography.Text>
                      <Typography.Text>
                        {users.find((user) => user.id === item.approver.id)?.full_name || '-'}
                      </Typography.Text>
                    </Space>
                  </List.Item>
                )}
                style={{ marginBottom: 24 }}
              />
            )}
          </Form.Col>
          <GeneralRequestJobPostingForm form={form} users={users} {...formArgs} />
          <Form.Footer>
            <Form.FooterEnd>
              <Button
                onClick={() =>
                  navigate({ pathname: '/:tenant/request-job-postings/', params: { tenant } })
                }
              >
                Cancelar
              </Button>
              <Button loading={mutation.isLoading} type="primary" htmlType="submit">
                Salvar e iniciar fluxo
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </PageContent>
    </Page>
  );
};

export default withRouteChange()(CreateRequestJobPosting);
